import { accountingApi } from '../../../../config/apiClient';

/**
 * Basit üretim reçeteleri ve ürün menüleri için listeleme servisi.
 *
 * Liste uçları server-side sayfalıdır: `/all` uçları tüm kayıtları tek seferde
 * döndürdüğü için liste ekranlarında kullanılmaz.
 *
 * ⚠️ CompanyId gönderilmez — backend şirketi JWT claim'inden çözer
 * (bkz. CRITICAL_API_DEVELOPMENT_RULES → Yöntem 1).
 */

export interface ProductRecipeListItem {
  id: string;
  inventoryItemId: string;
  inventoryItemName?: string | null;
  inventoryItemBarcode?: string | null;
  inventoryItemStockCode?: string | null;
  recipeName: string;
  recipeCode?: string | null;
  description?: string | null;
  outputQuantity: number;
  outputUnit: string;
  ingredientCount: number;
  /** Fire dahil toplam malzeme maliyeti (alt reçeteler çözülmüş). */
  totalCost: number;
  /** Çıktı birimi başına maliyet. */
  unitCost: number;
  allowNegativeStock: boolean;
  isFlatteningRequired: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

export interface ProductMenuListItem {
  id: string;
  inventoryItemId: string;
  inventoryItemName?: string | null;
  inventoryItemBarcode?: string | null;
  inventoryItemStockCode?: string | null;
  menuName: string;
  menuCode?: string | null;
  description?: string | null;
  itemCount: number;
  optionGroupCount: number;
  allowNegativeStock: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

export interface PagedListResult<T> {
  items: T[];
  totalCount: number;
}

/**
 * Liste ekranı özet kartları. Filtreden bağımsız, şirket geneli toplamlar.
 * `ingredientCount` reçetelerde, `itemCount` menülerde dolu gelir.
 */
export interface ProductListSummary {
  totalCount: number;
  activeCount: number;
  inactiveCount: number;
  ingredientCount?: number;
  itemCount?: number;
}

export interface PagedListQuery {
  page: number;
  pageSize: number;
  search?: string;
  /** null/undefined = hepsi, true = aktif, false = pasif */
  isActive?: boolean | null;
  /** recipeName | menuName | itemName | createdAt | updatedAt */
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
}

const buildParams = (query: PagedListQuery) => {
  const params: Record<string, string | number | boolean> = {
    page: query.page,
    pageSize: query.pageSize,
  };
  if (query.search && query.search.trim()) params.search = query.search.trim();
  if (query.isActive === true || query.isActive === false) params.isActive = query.isActive;
  if (query.sortColumn) params.sortColumn = query.sortColumn;
  if (query.sortDirection) params.sortDirection = query.sortDirection;
  return params;
};

export class ProductRecipeMenuService {
  static async getPagedRecipes(query: PagedListQuery): Promise<PagedListResult<ProductRecipeListItem>> {
    const response = await accountingApi.get('/api/ProductRecipe/paged', {
      params: buildParams(query),
    });
    return {
      items: response.data?.items || [],
      totalCount: response.data?.totalCount || 0,
    };
  }

  static async getPagedMenus(query: PagedListQuery): Promise<PagedListResult<ProductMenuListItem>> {
    const response = await accountingApi.get('/api/ProductMenu/paged', {
      params: buildParams(query),
    });
    return {
      items: response.data?.items || [],
      totalCount: response.data?.totalCount || 0,
    };
  }

  static async getRecipeSummary(): Promise<ProductListSummary> {
    const response = await accountingApi.get('/api/ProductRecipe/summary');
    return {
      totalCount: response.data?.totalCount || 0,
      activeCount: response.data?.activeCount || 0,
      inactiveCount: response.data?.inactiveCount || 0,
      ingredientCount: response.data?.ingredientCount || 0,
    };
  }

  static async getMenuSummary(): Promise<ProductListSummary> {
    const response = await accountingApi.get('/api/ProductMenu/summary');
    return {
      totalCount: response.data?.totalCount || 0,
      activeCount: response.data?.activeCount || 0,
      inactiveCount: response.data?.inactiveCount || 0,
      itemCount: response.data?.itemCount || 0,
    };
  }
}

export default ProductRecipeMenuService;
